import { useState, useEffect, useRef, useMemo } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { toast } from 'react-toastify'
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Server from "../../Shared/Server"
import { FaFileExport } from "react-icons/fa";
import Spinner from '../../Shared/Spinner'
import SelectField from "../../Shared/SelectField";
import {years_list, months_list, categories_with_serverside} from "../../Shared/List";

import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const UnBilled = (props) => {
    const current_user = JSON.parse(localStorage.getItem('user'))
    const billing_date  = current_user?.branch?.billing_date

    const [errors, setErrors] = useState({});
    const [unbilled, setUnBilled] = useState([]);
    const [pinnedBottomRowData, setPinnedBottomRowData] = useState([]);
    const [yearOptions, setYearOptions] = useState([]);
    const [monthOptions, setMonthOptions] = useState([]);
    const [byOptions, setByOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [show, setShow] = useState(false);
    const [publicID, setPublicID] = useState();
    const [title, setTitle] = useState('');
    const gridRef = useRef();
    const navigate = useNavigate();
    const location = useLocation();
    const date = new Date().toISOString().slice(0, 10);

    const [formData, setFormData] = useState({
        year: billing_date?.year || date.year,
        month: billing_date?.month || date.month,
        by: 1,
    })
    const {year, month, by} = formData
    const fetchData = async (payload) => {
        setIsLoading(true)
        try {
            const response = await Server.post('/un-billed/', payload)
            const years = []
            const months = []
            const categories = []

            years_list.map(year => (
                years.push({
                    id: year.id,
                    key: year.key,
                    name: year.name
                })
            ))

            months_list.map(month => (
                months.push({
                    id: month.id,
                    key: month.key,
                    name: month.name
                })
            ))

            categories_with_serverside.map(category => (
                categories.push({
                    id: category.id,
                    key: category.key,
                    name: category.name
                })
            ))

            setUnBilled(response?.data)
            setYearOptions(years)
            setMonthOptions(months)
            setByOptions(categories)
            setIsLoading(false)

            const output = response?.data.reduce((data, row) => {
                data.customers += row.customers;
                return data;
            }, {customers: 0});
            setPinnedBottomRowData([
                {
                    customers: output.customers,
                }
            ]);
        } catch (error) {
            setIsLoading(false)
            if(error?.response?.status === 401){
                localStorage.removeItem('user')
                navigate('/login')
            }else if (error?.response?.status === 422) {
                setErrors({})
                error.response?.data?.detail.map((err) => (
                    setErrors((prevState) => ({
                        ...prevState,
                        [err.loc[1]]: err.msg
                    }))
                ))
            }else {
                toast.error(error?.response?.data?.detail || 'Failed to fetch unbilled data')
            }
        }
    }

    useEffect(() => {
        if (location.state?.year && location.state?.month) {
            const matchedCategory = typeof location.state.by === 'string'
                ? categories_with_serverside.find(c => c.name.toLowerCase() === location.state.by.toLowerCase())
                : null;
            const byId = matchedCategory?.id || (typeof location.state.by === 'number' ? location.state.by : 1);
            const byStr = matchedCategory?.name || categories_with_serverside[byId - 1].name;
            setFormData(prev => ({
                ...prev,
                year: location.state.year,
                month: location.state.month,
                by: byId,
            }))
            const monthName = months_list[location.state.month - 1].name
            setTitle(`${monthName}, ${location.state.year} UnBilled By ${byStr}`);
            fetchData({ year: location.state.year, month: location.state.month, by: byStr })
        } else {
            const monthName = months_list[formData.month - 1].name
            const categoryName = categories_with_serverside[formData.by - 1].name
            setTitle(`${monthName}, ${year} UnBilled By ${categoryName}`);
            fetchData({year: year, month: month, by: categoryName})
        }
    }, [])

    const columnDefs = useMemo(() => [
        {
            headerName: "#",
            filter: false,
            maxWidth: 70,
            sortable: true,
            valueFormatter: function (params) {
                if (params.node.rowPinned) return '';
                if (isNaN(params.node.id)) {
                    return "";
                }
                return `${Number(params.node.rowIndex) + 1}`;
            },
        },
        {
            headerName: "Name",
            field: "name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Customers",
            field: "customers",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
    ], []);

    const handleClose = () => {
        if (isDeleting) return;
        setShow(false)
    }
    const handleShow = (public_id) => {
        setShow(true)
        setPublicID(public_id)
    };

    const handleDelete = async (public_id) => {
        setIsDeleting(true)
        try {
            setIsLoading(true)
            const response = await Server.delete(`/un-billed/${public_id}`)
            if (response.data.status_code === 200){
                toast.success(response.data.detail)
                setUnBilled((prev) => prev.filter((bill) => bill.public_id !== public_id))
            }else{
                toast.error(response.data.detail)
            }
        } catch (error) {
            if(error?.response?.status === 401){
                navigate('/login')
            }else{
                toast.error(error?.response?.data?.detail || 'Failed to delete record')
            }
        } finally {
            setIsDeleting(false)
            setIsLoading(false)
            setShow(false)
        }
      }

      const onChange = (e) => {
        setFormData((prevState) => ({
            ...prevState,
            [e.target.name]: e.target.value
        }))

    }

    const onDoubleClick = (payload) => {
        navigate(`/un-billed-${payload.by}`, { state: {payload: payload}})
    }

    const onSubmit = (e) => {
        e.preventDefault()

        const monthName = months_list[formData.month - 1].name
        const categoryName = categories_with_serverside[formData.by - 1].name
        setTitle(`${monthName}, ${year} UnBilled By ${categoryName}`);
        if (categoryName === 'ServerSide'){
            const payload = {year, month, by: 'serverside', name: 'ServerSide'}
            navigate(`/un-billed-serverside`, { state: {payload: payload}})
        }else{
            const payload = {year, month, by: categoryName}
            fetchData(payload)
        }

    }

    const defaultColDef = useMemo(() => {
        return {
            filterParams: {
                buttons: []
            }
        };
    }, []);

    const onFirstDataRendered = (params) => {
        params.api.autoSizeAllColumns();
    }

    const onGridSizeChanged = (params) => {
        params.api.autoSizeAllColumns();
    }

    const gridApiRef = useRef(null);

    const onGridReady = (params) => {
        gridApiRef.current = params.api;
    };

    const onExportClick = () => {
        if (gridApiRef.current) {
            gridApiRef.current.exportDataAsCsv({ fileName: `${title}.csv` });
        } else {
            console.error("Grid API is not available yet!");
        }
    };

    const getRowStyle = (params) => {
        if (params.node.rowPinned) {
          return {
            backgroundColor: '#f8f9fa',
            fontWeight: 'bold',
            color: '#000',
          };
        }
        return {};
    };

    if(isLoading){
        return <Spinner/>
    }

  return (
    <>
        <form onSubmit={onSubmit}>
            <div className="row">
                <div className="col-md-2">
                    <SelectField
                        name="year"
                        value={parseInt(year)}
                        options={yearOptions}
                        onChange={onChange}
                        error={errors.year}
                    />
                </div>
                <div className="col-md-2">
                    <SelectField
                        name="month"
                        value={parseInt(month)}
                        options={monthOptions}
                        onChange={onChange}
                        error={errors.month}
                    />
                </div>
                <div className="col-md-2">
                    <SelectField
                        name="by"
                        value={parseInt(by)}
                        options={byOptions}
                        onChange={onChange}
                        error={errors.by}
                    />
                </div>
                <div className="col-md-4">
                    <input type="submit" className="btn btn-info mt-1" value="Apply Filter" style={{float: 'left'}}/>
                </div>
                <div className="col-md-2">
                    <button onClick={()=>onExportClick()} className="btn btn-success mb-2" style={{float: 'right'}}><FaFileExport/> &nbsp;Export</button>
                </div>
            </div>
            <br/>
        </form>
        <div className="card mt-0">
            <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
                    <h3 className="card-title">{title}</h3>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-12">
                        <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            onRowDoubleClicked={(e) =>{
                                if (e.node.rowPinned) return '';
                                if (e.data.customers < 1) return '';
                                onDoubleClick({
                                    year: parseInt(year),
                                    month: parseInt(month),
                                    public_id: e.data.public_id,
                                    by: categories_with_serverside[formData.by - 1].name.toLowerCase(),
                                    name: e.data.name
                                })
                            }}
                            suppressMenuHide={true}
                            defaultColDef={defaultColDef}
                            onFirstDataRendered={onFirstDataRendered}
                            onGridSizeChanged={onGridSizeChanged}
                            rowData={unbilled}
                            domLayout={"normal"}
                            rowSelection={"multiple"}

                            animateRows={true}

                            pagination={false}
                            onGridReady={onGridReady}

                            resizable={true}
                            enableCellTextSelection={true}
                            suppressCellFocus={true}
                            ref={gridRef}
                            pinnedBottomRowData={pinnedBottomRowData}
                            getRowStyle={getRowStyle}
                            columnDefs={columnDefs}
                        ></AgGridReact>{" "}
                    </div>
                    </div>
                </div>
            </div>
        </div>

        <Modal show={show} onHide={handleClose} backdrop={isDeleting ? 'static' : true}>
        <Modal.Header closeButton={!isDeleting}>
          <Modal.Title>Delete Alert</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure?</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleClose} disabled={isDeleting}>
            No
          </Button>
          <Button variant="danger" onClick={() => handleDelete(publicID)} disabled={isDeleting}>
            {isDeleting ? 'Processing…' : 'Yes'}
          </Button>
        </Modal.Footer>
      </Modal>
      <br/>
    </>
  )
}

export default UnBilled